import EventUnavailable from '@/components/EventUnavailable';
import CartTimer from '@/features/checkout/components/CartTimer';
import ShoppingCart from '@/features/checkout/components/ShoppingCart';
import SeatMap from '@/features/events/components/SeatMap';
import TableSeatPicker from '@/features/events/components/TableSeatPicker';
import { useEventSeats } from '@/hooks/useEventSeats';
import { useToast } from '@/hooks/useToast';
import { API_BASE_URL, api } from '@/lib/api';
import { formatEventDate, formatEventTime } from '@/lib/utils';
import type { Event, LayoutData, Seat } from '@eventflow/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { Eye, Lightbulb, Ticket } from 'lucide-react';
import { nanoid } from 'nanoid';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

export default function EventPublicPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const seatsQueryKey = ['events', id, 'seats'] as const;

  const [event, setEvent] = useState<Event | null>(null);
  const [selectedSeatIds, setSelectedSeatIds] = useState<string[]>([]);
  const [isEventLoading, setIsEventLoading] = useState(true);
  const [error, setError] = useState('');
  const [lockingSeats, setLockingSeats] = useState<Set<string>>(new Set()); // Track individual seats being locked
  const [lockExpiresAt, setLockExpiresAt] = useState<string | null>(null);
  const [isTableOpen, setIsTableOpen] = useState(false);
  const [activeTableId, setActiveTableId] = useState<string | null>(null);
  const [activeTableLabel, setActiveTableLabel] = useState<string>('');
  const [recentlyReleasedSeatIds, setRecentlyReleasedSeatIds] = useState<Set<string>>(new Set());

  const seatsQuery = useEventSeats(id);
  const seats = seatsQuery.data ?? [];
  const isLoading = isEventLoading || seatsQuery.isLoading;

  // Debounce control for lock/release sync
  const DEBOUNCE_MS = 300;
  const lockSyncTimeoutRef = useRef<number | null>(null);
  const pendingReleaseRef = useRef<Set<string>>(new Set());
  const selectedSeatIdsRef = useRef<string[]>([]);

  const {
    success: toastSuccess,
    error: toastError,
    warning: toastWarning,
    info: toastInfo,
    ToastContainer,
  } = useToast();

  // Session ID único para este usuario/pestaña
  const sessionIdRef = useRef<string>(localStorage.getItem('eventflow_session_id') || nanoid());

  // Guardar sessionId en localStorage para persistencia
  useEffect(() => {
    localStorage.setItem('eventflow_session_id', sessionIdRef.current);
  }, []);

  // Enviar estadísticas de asientos al editor de mapas para exportación con estadísticas
  useEffect(() => {
    try {
      const reservedPending = seats.filter(
        (s) => s.status === 'locked' && !s.locked_by_session
      ).length;
      const confirmed = seats.filter((s) => s.status === 'sold').length;
      const available = seats.filter((s) => s.status === 'available').length;
      window.dispatchEvent(
        new CustomEvent('mapEditor:updateSeatStats', {
          detail: { reservedPending, confirmed, available },
        })
      );
    } catch {
      // No interrumpir la UI por errores de despacho
    }
  }, [seats]);

  // Mantener un ref con los asientos seleccionados más recientes
  useEffect(() => {
    selectedSeatIdsRef.current = selectedSeatIds;
  }, [selectedSeatIds]);

  // Ref para evitar que cleanupOldLocks se ejecute múltiples veces (React StrictMode)
  const cleanupRanRef = useRef(false);

  // Limpiar locks de esta sesión al montar (por si quedaron de una carga anterior)
  // Solo ejecutar si el evento está cargado y tiene layout_data (evento activo)
  useEffect(() => {
    const cleanupOldLocks = async () => {
      if (!id || !event?.layout_data) return;

      // Evitar ejecutar múltiples veces (React StrictMode monta 2 veces)
      if (cleanupRanRef.current) return;

      // Si ya hay asientos seleccionados, no limpiar
      if (selectedSeatIdsRef.current.length > 0) return;

      cleanupRanRef.current = true;

      try {
        const allSeats = await api.get<Seat[]>(`/events/${id}/seats`);
        const lockedByThisSession = allSeats.filter(
          (s) => s.status === 'locked' && s.locked_by_session === sessionIdRef.current
        );

        if (lockedByThisSession.length > 0) {
          await api.post('/seats/release', {
            seatIds: lockedByThisSession.map((s) => s.id),
            sessionId: sessionIdRef.current,
          });
          await seatsQuery.refetch();
        }
      } catch {
        // Silenciar error - es una limpieza en background que no afecta la UI
      }
    };

    cleanupOldLocks();
  }, [id, event?.layout_data, seatsQuery]);

  // Fetch event and seats
  useEffect(() => {
    if (!id) return;

    const fetchData = async () => {
      try {
        setIsEventLoading(true);

        // Fetch event details
        const eventData = await api.get<Event>(`/events/${id}`);
        setEvent(eventData);

        // Si el evento no está publicado y no somos organizadores, no cargar asientos
        // El backend ya eliminó layout_data, así que verificamos eso
        if (!eventData.layout_data && !(eventData as any).isOrganizer) {
          return;
        }
      } catch (err) {
        console.error('Error fetching event:', err);
        setError(err instanceof Error ? err.message : 'Error al cargar evento');
      } finally {
        setIsEventLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Handle seat selection with automatic locking
  const handleSeatClick = async (seat: Seat) => {
    // Prevenir re-clicks mientras se procesa
    if (lockingSeats.has(seat.id)) return;

    const isCurrentlySelected = selectedSeatIds.includes(seat.id);
    const sessionId = sessionIdRef.current;

    if (seat.status === 'blocked') {
      toastWarning('Este asiento no está disponible para compra');
      return;
    }

    if (seat.status === 'locked' && seat.locked_by_session !== sessionId) {
      toastWarning('Este asiento ya está siendo seleccionado por otro usuario');
      return;
    }

    // Marcar este asiento como "en proceso"
    setLockingSeats((prev) => new Set(prev).add(seat.id));

    try {
      if (isCurrentlySelected) {
        // DESELECCIONAR: Remover de la lista y marcar para release
        const remainingSeats = selectedSeatIds.filter((id) => id !== seat.id);
        setSelectedSeatIds(remainingSeats);
        pendingReleaseRef.current.add(seat.id);

        queryClient.setQueryData(seatsQueryKey, (prev) => {
          const current = (prev ?? []) as Seat[];
          return current.map((s) =>
            s.id === seat.id
              ? {
                  ...s,
                  status: 'available',
                  locked_by_session: undefined,
                  locked_until: undefined,
                }
              : s
          );
        });

        toastInfo(`Asiento ${seat.seat_identifier} liberado`);
      } else {
        // SELECCIONAR: Agregar a la lista
        if (selectedSeatIds.length >= 36) {
          toastWarning('Puedes seleccionar máximo 36 asientos');
          setLockingSeats((prev) => {
            const next = new Set(prev);
            next.delete(seat.id);
            return next;
          });
          return;
        }
        const newSelectedIds = [...selectedSeatIds, seat.id];
        setSelectedSeatIds(newSelectedIds);
        // Si estaba pendiente de release, cancelarlo
        pendingReleaseRef.current.delete(seat.id);

        queryClient.setQueryData(seatsQueryKey, (prev) => {
          const current = (prev ?? []) as Seat[];
          const lockedUntil = new Date(Date.now() + 1 * 60 * 1000).toISOString();
          return current.map((s) =>
            s.id === seat.id
              ? {
                  ...s,
                  status: 'locked',
                  locked_by_session: sessionId,
                  locked_until: lockedUntil,
                }
              : s
          );
        });

        toastSuccess(`Asiento ${seat.seat_identifier} seleccionado`);
      }

      // Programar sync debounced (release + lock conjunto)
      if (lockSyncTimeoutRef.current) {
        clearTimeout(lockSyncTimeoutRef.current);
      }

      lockSyncTimeoutRef.current = window.setTimeout(async () => {
        lockSyncTimeoutRef.current = null;

        try {
          // PASO 1: Liberar asientos deseleccionados
          const toRelease = Array.from(pendingReleaseRef.current);
          if (toRelease.length > 0) {
            try {
              await api.post('/seats/release', {
                seatIds: toRelease,
                sessionId,
              });

              queryClient.setQueryData(seatsQueryKey, (prev) => {
                const current = (prev ?? []) as Seat[];
                const toReleaseSet = new Set(toRelease);
                return current.map((s) =>
                  toReleaseSet.has(s.id)
                    ? {
                        ...s,
                        status: 'available',
                        locked_by_session: undefined,
                        locked_until: undefined,
                      }
                    : s
                );
              });

              pendingReleaseRef.current.clear();
              // Mantenerlos en cyan por un pequeño lapso para evitar flicker
              setRecentlyReleasedSeatIds((prev) => {
                const next = new Set(prev);
                toRelease.forEach((id) => next.add(id));
                return next;
              });
              window.setTimeout(() => {
                setRecentlyReleasedSeatIds((prev) => {
                  const next = new Set(prev);
                  toRelease.forEach((id) => next.delete(id));
                  return next;
                });
              }, 500);
            } catch (releaseErr) {
              console.error('[Sync] Error al liberar:', releaseErr);
              // Continuar con el lock aunque falle el release
            }
          }

          // PASO 2: Bloquear asientos seleccionados
          const currentSelected = selectedSeatIdsRef.current;
          if (currentSelected.length > 0) {
            const response = await api.post<{ lockedSeats: Seat[]; expiresAt: string }>(
              '/seats/lock',
              {
                seatIds: currentSelected,
                sessionId,
                lockDurationMinutes: 1, // Tiempo de reserva: 10 minutos
              }
            );

            queryClient.setQueryData(seatsQueryKey, (prev) => {
              const current = (prev ?? []) as Seat[];
              const lockedById = new Map(response.lockedSeats.map((s) => [s.id, s] as const));
              return current.map((s) => lockedById.get(s.id) ?? s);
            });

            setLockExpiresAt(response.expiresAt);
          } else {
            // Si no hay asientos seleccionados, limpiar el timer
            setLockExpiresAt(null);
          }
        } catch (syncErr) {
          // Determinar si es un flujo esperado (asientos tomados por otro) o error de sistema
          const isExpectedConflict =
            syncErr instanceof Error &&
            (syncErr.message.includes('tomados') ||
              syncErr.message.includes('tomado') ||
              syncErr.message.includes('no están disponibles'));

          if (isExpectedConflict) {
            toastWarning('Algunos asientos acaban de ser seleccionados por otro usuario');
          } else {
            console.error('[Sync] Error en sync de locks:', syncErr);
            toastError('Error al sincronizar asientos');
          }
          // Refrescar estado desde el servidor y revertir selección a solo los asientos que quedaron bloqueados por esta sesión
          if (id) {
            try {
              await seatsQuery.refetch();
              const sessionIdNow = sessionIdRef.current;
              const refreshed = (seatsQuery.data ?? []) as Seat[];
              const stillMine = refreshed
                .filter(
                  (s) =>
                    selectedSeatIdsRef.current.includes(s.id) &&
                    s.locked_by_session === sessionIdNow
                )
                .map((s) => s.id);
              setSelectedSeatIds(stillMine);
            } catch (refreshErr) {
              console.error('[Sync] Error al refrescar asientos:', refreshErr);
            }
          }
        } finally {
          // Limpiar indicadores de procesamiento
          setLockingSeats(new Set());
        }
      }, DEBOUNCE_MS);
    } catch (err) {
      // Determinar si es flujo esperado (conflicto de asientos) o error de sistema
      const isExpectedConflict =
        err instanceof Error &&
        (err.message.includes('ya no están disponibles') ||
          err.message.includes('ya fueron tomados'));

      if (isExpectedConflict) {
        toastError('Este asiento acaba de ser tomado por otro usuario');
      } else {
        console.error('Error al bloquear/liberar asiento:', err);
        if (err instanceof Error) {
          toastError('Error: ' + err.message);
        }
      }

      // Revertir optimistic update en caso de error
      if (!isCurrentlySelected) {
        setSelectedSeatIds((prev) => prev.filter((id) => id !== seat.id));
      } else {
        setSelectedSeatIds((prev) => [...prev, seat.id]);
      }

      // Recargar asientos para obtener el estado actualizado
      if (id) {
        try {
          await seatsQuery.refetch();
        } catch (refreshErr) {
          console.error('Error refreshing seats:', refreshErr);
        }
      }
    } finally {
      // Indicador de procesamiento se limpia cuando corre el sync debounced
    }
  };

  const handleOpenTable = (tableId: string, tableLabel: string) => {
    setActiveTableId(tableId);
    setActiveTableLabel(tableLabel);
    setIsTableOpen(true);
  };

  const handleCloseTable = () => {
    setIsTableOpen(false);
    setActiveTableId(null);
    setActiveTableLabel('');
  };

  // Remove seat from selection (from cart)
  const handleRemoveSeat = async (seatId: string) => {
    try {
      const sessionId = sessionIdRef.current;
      const remainingSeats = selectedSeatIds.filter((id) => id !== seatId);
      setSelectedSeatIds(remainingSeats);
      pendingReleaseRef.current.add(seatId);
      setLockingSeats((prev) => new Set(prev).add(seatId));

      queryClient.setQueryData(seatsQueryKey, (prev) => {
        const current = (prev ?? []) as Seat[];
        return current.map((s) =>
          s.id === seatId
            ? {
                ...s,
                status: 'available',
                locked_by_session: undefined,
                locked_until: undefined,
              }
            : s
        );
      });

      // Programar sync debounced
      if (lockSyncTimeoutRef.current) clearTimeout(lockSyncTimeoutRef.current);
      lockSyncTimeoutRef.current = window.setTimeout(async () => {
        lockSyncTimeoutRef.current = null;
        try {
          const toRelease = Array.from(pendingReleaseRef.current);
          if (toRelease.length > 0) {
            await api.post('/seats/release', { seatIds: toRelease, sessionId });

            queryClient.setQueryData(seatsQueryKey, (prev) => {
              const current = (prev ?? []) as Seat[];
              const toReleaseSet = new Set(toRelease);
              return current.map((s) =>
                toReleaseSet.has(s.id)
                  ? {
                      ...s,
                      status: 'available',
                      locked_by_session: undefined,
                      locked_until: undefined,
                    }
                  : s
              );
            });

            pendingReleaseRef.current.clear();
          }
          const currentSelected = selectedSeatIdsRef.current;
          if (currentSelected.length > 0) {
            const response = await api.post<{ lockedSeats: Seat[]; expiresAt: string }>(
              '/seats/lock',
              {
                seatIds: currentSelected,
                sessionId,
                lockDurationMinutes: 10, // Tiempo de reserva: 10 minutos
              }
            );

            queryClient.setQueryData(seatsQueryKey, (prev) => {
              const current = (prev ?? []) as Seat[];
              const lockedById = new Map(response.lockedSeats.map((s) => [s.id, s] as const));
              return current.map((s) => lockedById.get(s.id) ?? s);
            });

            setLockExpiresAt(response.expiresAt);
          } else {
            setLockExpiresAt(null);
          }
        } catch (e) {
          console.error('[RemoveFromCart] Error en sync de locks:', e);
          toastError('Error al sincronizar asientos');
        } finally {
          setLockingSeats(new Set());
        }
      }, DEBOUNCE_MS);

      toastInfo('Asiento eliminado del carrito');
    } catch (err) {
      console.error('Error al liberar asiento:', err);
      toastError('Error al eliminar asiento');
      // Aún así remover del estado local
      setSelectedSeatIds((prev) => prev.filter((id) => id !== seatId));
    }
  };

  // Liberar todos los asientos SOLO al cerrar la pestaña (NO al navegar)
  useEffect(() => {
    // Liberar al cerrar la pestaña
    const handleBeforeUnload = () => {
      if (selectedSeatIds.length > 0) {
        navigator.sendBeacon(
          `${API_BASE_URL}/seats/release`,
          JSON.stringify({
            seatIds: selectedSeatIds,
            sessionId: sessionIdRef.current,
          })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [selectedSeatIds]);

  // Handle timer expiration
  const handleTimerExpire = async () => {
    toastError('¡Tiempo expirado! Tus asientos fueron liberados');

    // Liberar asientos en el backend
    const seatsToRelease = [...selectedSeatIdsRef.current];
    if (seatsToRelease.length > 0) {
      try {
        await api.post('/seats/release', {
          seatIds: seatsToRelease,
          sessionId: sessionIdRef.current,
        });
      } catch {
        // Silent fail - already showed toast
      }
    }

    setSelectedSeatIds([]);
    setLockExpiresAt(null);

    // TEMPORALMENTE DESACTIVADO: Cerrar el selector de asientos si está abierto
    handleCloseTable();

    // Recargar asientos
    if (id) {
      seatsQuery.refetch();
    }
  };

  // Proceed to checkout
  const handleCheckout = () => {
    if (selectedSeatIds.length === 0) {
      toastWarning('Selecciona al menos un asiento');
      return;
    }

    // Store selected seats in localStorage
    localStorage.setItem('selectedSeatIds', JSON.stringify(selectedSeatIds));
    localStorage.setItem('eventId', id || '');

    toastInfo('Redirigiendo al checkout...');

    // Navigate to checkout (los asientos permanecen bloqueados)
    navigate('/checkout');
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Cargando evento...</p>
        </div>
      </div>
    );
  }

  // Si hay error Y no hay evento, mostrar "Evento no encontrado"
  if (error && !event) {
    return <EventUnavailable reason="not_found" />;
  }

  // Si no hay evento (pero sin error explícito), también es "no encontrado"
  if (!event) {
    return <EventUnavailable reason="not_found" />;
  }

  const selectedSeats = seats.filter((seat) => selectedSeatIds.includes(seat.id));
  const availableCount = seats.filter((s) => s.status === 'available').length;
  const totalSeats = seats.length;

  // Verificar si el usuario es organizador del evento (viene del backend)
  const isOrganizer = (event as any).isOrganizer === true;
  const isPaused = event.is_published === false;
  // Evento sin layout_data significa que el backend lo ocultó (usuario no autorizado en evento pausado)
  const isHiddenForUser = !event.layout_data && !isOrganizer;
  const isSoldOut = totalSeats > 0 && availableCount === 0 && selectedSeatIds.length === 0;

  // Mostrar "Cupo lleno" si:
  // 1. El evento está pausado (no publicado) Y el usuario NO es organizador
  // 2. El layout_data fue ocultado por el backend (evento pausado, usuario no autorizado)
  // 3. No hay asientos disponibles (sold out) Y el usuario NO es organizador
  // Los organizadores siempre ven el preview del evento
  if ((isPaused || isHiddenForUser || isSoldOut) && !isOrganizer) {
    return <EventUnavailable reason="sold_out" />;
  }

  if (!event.layout_data || !event.layout_data.shapes || event.layout_data.shapes.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            Este evento aún no tiene mapa de asientos
          </h3>
          <p className="mt-1 text-gray-500">El organizador está trabajando en configurar el mapa</p>
          <button onClick={() => navigate('/eventos')} className="btn-primary mt-4">
            Volver a eventos
          </button>
        </div>
      </div>
    );
  }

  // Array de asientos ordenados (no usado actualmente)

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
      {/* Event Header */}
      <div className="mb-6 sm:mb-8">
        <button
          onClick={() => navigate('/eventos')}
          className="text-sm text-gray-600 hover:text-gray-900 mb-3 sm:mb-4 flex items-center"
        >
          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Volver a eventos
        </button>

        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
          {event.name}
        </h1>

        {event.description && (
          <p className="text-sm sm:text-base text-gray-600 mb-3 sm:mb-4">{event.description}</p>
        )}

        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 text-sm text-gray-700">
          <div className="flex items-center">
            <svg
              className="h-5 w-5 mr-2 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span className="capitalize">{formatDate(event.event_date)}</span>
          </div>

          <div className="flex items-center">
            <svg
              className="h-5 w-5 mr-2 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span>{event.venue_name}</span>
          </div>

          <div className="flex items-center">
            <svg
              className="h-5 w-5 mr-2 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="font-semibold text-green-600">
              {availableCount} asientos disponibles
            </span>
          </div>
        </div>
      </div>

      {/* Aviso de previsualización para organizadores en eventos no publicados */}
      {!event.is_published && (event as any)?.organizers && (
        <div className="mb-6 rounded-lg border border-yellow-300 bg-yellow-50 p-3 text-yellow-900 flex items-center gap-2">
          <Eye className="w-4 h-4" />
          <span className="font-semibold">Previsualizando (no publicado)</span>
        </div>
      )}

      {/* Instrucciones de uso */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
        <div className="flex items-start gap-2 sm:gap-3">
          <div className="flex-shrink-0 hidden sm:block">
            <svg
              className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-xs sm:text-sm font-semibold text-gray-900 mb-1.5 sm:mb-2 flex items-center gap-1.5">
              <Ticket className="w-4 h-4 text-blue-600" /> Cómo seleccionar tus asientos:
            </h3>
            <ol className="text-xs sm:text-sm text-gray-700 space-y-1 sm:space-y-1.5 list-decimal list-inside">
              <li>
                <strong>Toca una mesa</strong> para ver sus asientos
              </li>
              <li>
                <strong>Selecciona los asientos</strong> que deseas
              </li>
              <li>
                <strong>Revisa tu selección</strong> en el carrito
              </li>
              <li>
                <strong>Procede al pago</strong> (10 min límite)
              </li>
            </ol>
            <p className="text-xs text-gray-600 mt-1.5 sm:mt-2 hidden sm:flex items-center gap-1">
              <Lightbulb className="w-3.5 h-3.5" /> <strong>Tip:</strong> Los asientos se reservan
              automáticamente por 10 minutos.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content: Map + Cart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
        {/* Seat Map */}
        <div className="lg:col-span-2 order-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 relative">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
              Selecciona tus Asientos
              {lockingSeats.size > 0 && (
                <span className="ml-2 text-xs sm:text-sm text-gray-500">
                  ({lockingSeats.size} procesando...)
                </span>
              )}
            </h2>

            <SeatMap
              layoutData={event.layout_data as LayoutData}
              seats={seats}
              selectedSeatIds={selectedSeatIds}
              isInteractive={true}
              lockingSeats={lockingSeats}
              onOpenTable={handleOpenTable}
              activeTableId={activeTableId || undefined}
              onResetZoom={() => {}} // Habilitar el mecanismo de reset
              eventInfo={{
                title: event.name,
                venue: event.venue_name,
                address: event.venue_address,
                date: event.event_date
                  ? formatEventDate(event.event_date, { includeTime: false })
                  : undefined,
                time: event.event_date ? formatEventTime(event.event_date) : undefined,
              }}
            />
          </div>
        </div>

        {/* Shopping Cart */}
        <div className="lg:col-span-1 space-y-3 sm:space-y-4 order-2 lg:order-2">
          {/* Timer */}
          {lockExpiresAt && selectedSeatIds.length > 0 && (
            <CartTimer expiresAt={lockExpiresAt} onExpire={handleTimerExpire} />
          )}

          {/* Table Seat Picker */}
          <TableSeatPicker
            isOpen={isTableOpen}
            tableId={activeTableId}
            tableLabel={activeTableLabel}
            seats={seats.filter(
              (s) =>
                (activeTableId ? s.table_id === activeTableId : false) ||
                (activeTableId ? s.seat_identifier === activeTableId : false) ||
                (activeTableId ? s.seat_identifier.startsWith(`${activeTableId}-`) : false)
            )}
            selectedSeatIds={selectedSeatIds}
            onSelectSeat={handleSeatClick}
            onClose={handleCloseTable}
            lockingSeats={lockingSeats}
            sessionId={sessionIdRef.current}
            onWarn={(msg) => toastWarning(msg)}
            recentlyReleasedSeatIds={recentlyReleasedSeatIds}
          />

          {/* Cart */}
          <ShoppingCart
            selectedSeats={selectedSeats}
            onRemoveSeat={handleRemoveSeat}
            onCheckout={handleCheckout}
          />
        </div>
      </div>

      {/* Toast Container */}
      <ToastContainer />
    </div>
  );
}
